<template>
	<div class="modal fade" id="transactionDetailModal" tabindex="-1" role="dialog" aria-hidden="true"
		data-backdrop="static">
		<div class="modal-dialog modal-xl modal-dialog-scrollable txn-detail-modal" role="document">
			<div class="modal-content">
				<div class="modal-header py-2 txn-detail-modal__header">
					<h5 class="modal-title mb-0" v-if="loaded">
						<span class="txn-type mr-2">{{ transaction.transaction_type }}</span>
						{{ transaction.transaction_no }}
					</h5>
					<div v-else class="txn-skeleton txn-skeleton--title"></div>
					<button type="button" class="close" @click="close" aria-label="Close">
						<span aria-hidden="true">&times;</span>
					</button>
				</div>

				<div class="modal-body txn-detail-modal__body">
					<div v-if="!loaded && !loadError" class="txn-detail-skeleton" aria-busy="true" aria-label="Loading transaction">
						<div class="txn-view-panel txn-detail-skeleton__hero mb-3">
							<div class="d-flex justify-content-between mb-2">
								<div class="txn-skeleton txn-skeleton--badge"></div>
								<div class="txn-skeleton txn-skeleton--amount"></div>
							</div>
							<div class="txn-skeleton txn-skeleton--line txn-skeleton--line-sm"></div>
						</div>
						<div class="txn-detail-modal__actions mb-3">
							<div class="txn-detail-modal__status">
								<div class="txn-skeleton txn-skeleton--label mb-2"></div>
								<div class="txn-skeleton txn-skeleton--input"></div>
							</div>
							<div class="txn-detail-modal__receipt">
								<div class="txn-skeleton txn-skeleton--label mb-2"></div>
								<div class="txn-skeleton txn-skeleton--btn"></div>
							</div>
						</div>
						<div class="row">
							<div class="col-lg-4 mb-3 mb-lg-0">
								<div class="txn-view-panel">
									<div class="txn-skeleton txn-skeleton--label mb-3"></div>
									<div class="txn-skeleton txn-skeleton--photo mx-auto mb-3"></div>
									<div class="txn-skeleton txn-skeleton--line mx-auto mb-2"></div>
									<div class="txn-skeleton txn-skeleton--line txn-skeleton--line-sm mx-auto mb-3"></div>
									<div class="txn-skeleton txn-skeleton--line mb-2" v-for="n in 4" :key="'d-' + n"></div>
								</div>
							</div>
							<div class="col-lg-8">
								<div class="txn-view-panel mb-3">
									<div class="txn-skeleton txn-skeleton--label mb-3"></div>
									<div class="txn-skeleton txn-skeleton--table-head mb-2"></div>
									<div class="txn-skeleton txn-skeleton--table-row mb-2" v-for="n in 3" :key="'t-' + n"></div>
								</div>
								<div class="txn-view-panel">
									<div class="txn-skeleton txn-skeleton--label mb-3"></div>
									<div class="txn-skeleton txn-skeleton--table-head mb-2"></div>
									<div class="txn-skeleton txn-skeleton--table-row mb-2" v-for="n in 4" :key="'l-' + n"></div>
								</div>
							</div>
						</div>
					</div>
					<p v-else-if="loadError" class="text-danger mb-0">{{ loadError }}</p>

					<template v-else-if="loaded">
						<div class="txn-view-hero mb-3" :class="typeCardClass(transaction.transaction_type)">
							<div class="txn-view-hero__top">
								<div class="txn-view-hero__badges">
									<span class="txn-type">{{ transaction.transaction_type }}</span>
									<span class="badge badge-pill" :class="statusBadgeClass(transaction.status)">
										{{ transaction.status }}
									</span>
								</div>
								<div class="txn-view-hero__amount">{{ formatCurrency(transaction.amount) }}</div>
							</div>
							<p class="txn-view-hero__date text-muted mb-0">
								{{ $formatDate(transaction.transaction_date_time, 'india') }}
							</p>
						</div>

						<div class="txn-detail-modal__actions">
							<div v-can="'transactions.change_status'" class="txn-detail-modal__status">
								<label class="txn-detail-modal__actions-label">Status</label>
								<div class="txn-status-update input-group input-group-sm">
									<select class="form-control form-control-sm" v-model="statusDraft">
										<option>Approved</option>
										<option>Rejected</option>
										<option>Pending</option>
									</select>
									<div class="input-group-append">
										<button type="button" class="btn btn-success btn-sm"
											@click="changeTransactionStatus"
											:disabled="statusDraft === transaction.status || statusUpdating">
											<i v-if="statusUpdating" class="fas fa-spinner fa-spin mr-1"></i>
											Update status
										</button>
									</div>
								</div>
							</div>
							<div v-if="transaction.status === 'Approved'" v-can="'transactions.download'"
								class="txn-detail-modal__receipt">
								<label class="txn-detail-modal__actions-label d-block">Receipt</label>
								<a target="_blank"
									:href="$store.getters.pdfUrl + 'api/downloadTransaction?transaction_id=' + transaction.transaction_id"
									class="btn btn-outline-primary btn-sm btn-block">
									<i class="fas fa-download mr-1"></i> Download receipt
								</a>
							</div>
						</div>

						<div class="row">
							<div class="col-lg-4 mb-3 mb-lg-0">
								<div class="txn-view-panel h-100">
									<div class="txn-view-panel__title">Customer</div>
									<div class="txn-view-customer-photo">
										<div class="txn-view-customer-photo__frame">
											<img v-if="customerAvatar" :src="customerAvatar"
												:alt="transaction?.customer?.customer_name || 'Customer photo'"
												class="txn-view-customer-photo__img" />
											<span v-else-if="customerInitial(transaction)" class="txn-view-customer-photo__initial">
												{{ customerInitial(transaction) }}
											</span>
										</div>
										<div class="txn-view-customer-photo__info text-center">
											<div v-if="transaction?.customer?.customer_no" class="txn-view-customer-photo__no">
												{{ transaction.customer.customer_no }}
											</div>
											<div v-if="transaction?.customer?.customer_name" class="txn-view-customer-photo__name">
												{{ transaction.customer.customer_name }}
											</div>
										</div>
									</div>
									<div class="txn-view-details">
										<div v-if="transaction?.payment_mode?.payment_mode" class="txn-detail">
											<span class="txn-detail__label">Payment mode</span>
											<span class="txn-detail__value">{{ transaction.payment_mode.payment_mode }}</span>
										</div>
										<div v-if="transaction?.user?.name" class="txn-detail">
											<span class="txn-detail__label">User</span>
											<span class="txn-detail__value">{{ transaction.user.name }}</span>
										</div>
										<div v-if="transaction.reference_id" class="txn-detail">
											<span class="txn-detail__label">Reference No</span>
											<span class="txn-detail__value">{{ transaction.reference_id }}</span>
										</div>
										<div class="txn-detail" v-if="transaction.promotion_value != 0">
											<span class="txn-detail__label">Promotion</span>
											<span class="txn-detail__value">{{ formatCurrency(transaction.promotion_value) }}</span>
										</div>
										<div class="txn-detail" v-if="transaction.promotion_value != 0">
											<span class="txn-detail__label">Total amount</span>
											<span class="txn-detail__value txn-detail__value--strong">
												{{ formatCurrency(transaction.total_amount) }}
											</span>
										</div>
										<div class="txn-detail" v-if="transaction.tds">
											<span class="txn-detail__label">TDS</span>
											<span class="txn-detail__value">{{ formatCurrency(transaction.tds) }}</span>
										</div>
										<div class="txn-detail" v-if="transaction.fee">
											<span class="txn-detail__label">Fee</span>
											<span class="txn-detail__value">{{ formatCurrency(transaction.fee) }}</span>
										</div>
										<div class="txn-detail" v-if="transaction.note">
											<span class="txn-detail__label">Note</span>
											<span class="txn-detail__value">{{ transaction.note }}</span>
										</div>
										<div class="txn-detail" v-if="transaction.attachment">
											<span class="txn-detail__label">Attachment</span>
											<span class="txn-detail__value">
												<a :href="transaction.attachment" target="_blank" rel="noopener" @click.stop>
													<i class="fas fa-paperclip mr-1"></i> Download
												</a>
											</span>
										</div>
									</div>
								</div>
							</div>

							<div class="col-lg-8">
								<div v-if="showDenominations" class="txn-view-panel mb-3">
									<div class="txn-view-panel__title">Denominations</div>
									<div class="table-responsive">
										<table class="table table-sm table-bordered txn-view-table mb-0">
											<thead>
												<tr>
													<th class="text-center" style="width: 48px;">#</th>
													<th>Denomination</th>
													<th class="text-center">Qty</th>
													<th class="text-right">Total</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="(row, key) in denominations" :key="row.transaction_denomination_id || key">
													<td class="text-center text-muted">{{ key + 1 }}</td>
													<td>{{ row?.denomination?.denomination_name }}</td>
													<td class="text-center">{{ row.quantity }}</td>
													<td class="text-right">{{ formatCurrency(row.total) }}</td>
												</tr>
												<tr v-if="!denominations.length">
													<td colspan="4" class="text-center text-muted py-3">No denominations recorded</td>
												</tr>
											</tbody>
											<tfoot v-if="denominations.length">
												<tr>
													<th colspan="3" class="text-right">Grand total</th>
													<th class="text-right">{{ formatCurrency(denominationsTotal) }}</th>
												</tr>
											</tfoot>
										</table>
									</div>
								</div>

								<div class="txn-view-panel">
									<div class="txn-view-panel__title">Activity log</div>
									<div class="table-responsive">
										<table class="table table-sm table-bordered txn-view-table mb-0">
											<thead>
												<tr>
													<th class="text-center" style="width: 48px;">#</th>
													<th>User</th>
													<th>Activity</th>
													<th class="text-center">Date & time</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="(log, key) in activityLogs" :key="log.transaction_log_id || key">
													<td class="text-center text-muted">{{ key + 1 }}</td>
													<td>{{ log?.user?.name }}</td>
													<td>{{ log.action }}</td>
													<td class="text-center text-nowrap">
														{{ $formatDate(log.created_at, 'india') }}
													</td>
												</tr>
												<tr v-if="!activityLogs.length">
													<td colspan="4" class="text-center text-muted py-3">No activity yet</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "TransactionDetailModal",

	data() {
		return {
			loaded: false,
			loadError: null,
			transaction: {},
			statusDraft: "Pending",
			statusUpdating: false,
		};
	},

	computed: {
		customerAvatar() {
			const avatar = this.transaction?.customer?.avatar;
			return avatar && avatar !== "null" && !String(avatar).endsWith("/customers/") ? avatar : null;
		},
		denominations() {
			return this.transaction.transaction_denominations || [];
		},
		activityLogs() {
			return this.transaction.transaction_logs || [];
		},
		showDenominations() {
			const type = this.transaction.transaction_type;
			return type === "BuyIn" || type === "CashOut";
		},
		denominationsTotal() {
			return this.denominations.reduce((sum, row) => sum + (Number(row.total) || 0), 0);
		},
	},

	mounted() {
		const vm = this;
		if (typeof window.$ !== "undefined") {
			window.$("#transactionDetailModal").on("hidden.bs.modal", () => {
				vm.loaded = false;
				vm.loadError = null;
				vm.transaction = {};
				vm.statusUpdating = false;
			});
		}
	},

	beforeUnmount() {
		if (typeof window.$ !== "undefined") {
			window.$("#transactionDetailModal").off("hidden.bs.modal");
		}
	},

	methods: {
		open(transactionId) {
			this.loaded = false;
			this.loadError = null;
			this.transaction = {};
			this.statusUpdating = false;
			this.fetchTransaction(transactionId);
			this.$nextTick(() => {
				if (typeof window.$ !== "undefined" && window.$("#transactionDetailModal").modal) {
					window.$("#transactionDetailModal").modal("show");
				}
			});
		},

		close() {
			if (typeof window.$ !== "undefined" && window.$("#transactionDetailModal").modal) {
				window.$("#transactionDetailModal").modal("hide");
			}
		},

		fetchTransaction(transactionId) {
			const vm = this;
			const uri = { uri: "showTransaction", data: { transaction_id: transactionId } };
			vm.$store
				.dispatch("post", uri)
				.then(function (response) {
					const data = vm.parseTransactionResponse(response);
					if (!data || !data.transaction_id) {
						vm.loadError = "Could not load transaction details.";
						vm.$store.dispatch("error", vm.loadError);
						return;
					}
					vm.transaction = data;
					vm.statusDraft = vm.transaction.status;
					vm.loaded = true;
				})
				.catch(function (error) {
					vm.loadError = error.response?.data?.message || "Unable to load transaction.";
					vm.$store.dispatch("error", vm.loadError);
				});
		},

		reloadActivityLogs() {
			const vm = this;
			if (!vm.transaction?.transaction_id) {
				return;
			}
			vm.$store
				.dispatch("post", {
					uri: "showTransaction",
					data: { transaction_id: vm.transaction.transaction_id },
				})
				.then(function (response) {
					const data = vm.parseTransactionResponse(response);
					if (data?.transaction_logs) {
						vm.transaction.transaction_logs = data.transaction_logs;
					}
				})
				.catch(function () {
					/* keep modal success; logs refresh is optional */
				});
		},

		formatCurrency(amount) {
			return new Intl.NumberFormat("en-IN", {
				style: "currency",
				currency: "INR",
			}).format(amount || 0);
		},

		statusBadgeClass(status) {
			if (status === "Approved") return "badge-success";
			if (status === "Rejected") return "badge-danger";
			if (status === "Pending") return "badge-warning";
			return "badge-secondary";
		},

		typeCardClass(transactionType) {
			const map = {
				Deposit: "txn-view-hero--deposit",
				Withdraw: "txn-view-hero--withdraw",
				BuyIn: "txn-view-hero--buyin",
				CashOut: "txn-view-hero--cashout",
				TDS: "txn-view-hero--tds",
			};
			return map[transactionType] || "txn-view-hero--default";
		},

		customerInitial(transaction) {
			const name = transaction?.customer?.customer_name;
			if (name) return name.charAt(0).toUpperCase();
			const no = transaction?.customer?.customer_no;
			if (no) return String(no).charAt(0).toUpperCase();
			return "";
		},

		parseTransactionResponse(response) {
			const body = response?.data;
			return body?.data ?? body;
		},

		changeTransactionStatus() {
			const vm = this;
			if (vm.statusUpdating) {
				return;
			}
			vm.statusUpdating = true;
			const payload = {
				transaction_id: vm.transaction.transaction_id,
				status: vm.statusDraft,
			};
			vm.$store
				.dispatch("post", { uri: "changeTransactionStatus", data: payload })
				.then(function (response) {
					const data = vm.parseTransactionResponse(response);
					const message = response.data?.message
						|| "Transaction status has been successfully updated";

					vm.transaction = {
						...vm.transaction,
						...(data && data.transaction_id ? data : { status: vm.statusDraft }),
					};
					vm.statusDraft = vm.transaction.status;
					vm.reloadActivityLogs();

					vm.$emit("updated", {
						transaction_id: vm.transaction.transaction_id,
						status: vm.transaction.status,
						amount: vm.transaction.amount,
						customer: vm.transaction.customer,
						payment_mode: vm.transaction.payment_mode,
						user: vm.transaction.user,
					});
					vm.$store.dispatch("success", message);
				})
				.catch(function (error) {
					vm.$store.dispatch("error", error.response?.data?.message || "Update failed");
				})
				.finally(function () {
					vm.statusUpdating = false;
				});
		},
	},
};
</script>

<style scoped>
.txn-detail-modal__header {
	align-items: center;
}

.txn-detail-modal__body {
	padding: 1rem 1.25rem;
	background: #f4f6f9;
}

.txn-detail-modal__actions {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 12px 16px;
	margin-bottom: 1rem;
	padding: 12px 14px;
	background: #fff;
	border: 1px solid #e3e6ea;
	border-radius: 8px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.txn-detail-modal__actions-label {
	display: block;
	font-size: 0.62rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #6c757d;
	margin-bottom: 6px;
}

.txn-detail-modal__status {
	flex: 1 1 260px;
	min-width: 220px;
}

.txn-detail-modal__receipt {
	flex: 0 0 180px;
	min-width: 160px;
}

.txn-status-update {
	width: 100%;
	max-width: 360px;
}

.txn-view-hero {
	background: #fff;
	border: 1px solid #e3e6ea;
	border-radius: 8px;
	border-top: 3px solid #6c757d;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	padding: 14px 16px;
}

.txn-view-hero--deposit { border-top-color: #28a745; }
.txn-view-hero--withdraw { border-top-color: #dc3545; }
.txn-view-hero--buyin { border-top-color: #007bff; }
.txn-view-hero--cashout { border-top-color: #17a2b8; }
.txn-view-hero--tds { border-top-color: #ffc107; }
.txn-view-hero--default { border-top-color: #6c757d; }

.txn-view-hero__top {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 6px;
}

.txn-view-hero__badges {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
}

.txn-type {
	font-size: 0.68rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #495057;
	background: #f1f3f5;
	padding: 2px 7px;
	border-radius: 4px;
}

.txn-view-hero__amount {
	font-size: 1.15rem;
	font-weight: 700;
	color: #212529;
}

.txn-view-hero__date {
	font-size: 0.85rem;
}

.txn-view-panel {
	background: #fff;
	border: 1px solid #e3e6ea;
	border-radius: 8px;
	padding: 12px 14px;
}

.txn-view-panel__title {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #6c757d;
	margin-bottom: 10px;
}

.txn-view-customer-photo {
	margin-bottom: 16px;
}

.txn-view-customer-photo__frame {
	width: 160px;
	height: 160px;
	margin: 0 auto 12px;
	border-radius: 10px;
	overflow: hidden;
	border: 3px solid #e9ecef;
	background: linear-gradient(135deg, #007bff, #0056b3);
	box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
	display: flex;
	align-items: center;
	justify-content: center;
}

.txn-view-customer-photo__img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.txn-view-customer-photo__initial {
	font-size: 3.5rem;
	font-weight: 700;
	color: #fff;
	line-height: 1;
}

.txn-view-customer-photo__no {
	font-size: 1.05rem;
	font-weight: 700;
	color: #212529;
	line-height: 1.25;
}

.txn-view-customer-photo__name {
	font-size: 0.9rem;
	color: #6c757d;
	margin-top: 4px;
	word-break: break-word;
}

.txn-view-details {
	display: grid;
	gap: 8px;
}

.txn-detail__label {
	display: block;
	font-size: 0.62rem;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #adb5bd;
	font-weight: 600;
	margin-bottom: 2px;
}

.txn-detail__value {
	display: block;
	font-size: 0.85rem;
	color: #343a40;
	font-weight: 500;
	word-break: break-word;
}

.txn-detail__value--strong {
	font-weight: 700;
}

.txn-view-table thead th {
	font-size: 0.7rem;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
	background: #f8f9fa;
}

.txn-view-table tbody td {
	font-size: 0.85rem;
	vertical-align: middle;
}

.txn-view-table tfoot th {
	background: #f8f9fa;
	font-size: 0.85rem;
}

.txn-detail-skeleton__hero {
	padding: 14px 16px;
}

.txn-skeleton {
	display: block;
	background: linear-gradient(90deg, #e9ecef 0%, #f5f6f8 50%, #e9ecef 100%);
	background-size: 200% 100%;
	animation: txn-skeleton-shimmer 1.2s ease-in-out infinite;
	border-radius: 4px;
}

.txn-skeleton--title {
	width: 220px;
	height: 22px;
}

.txn-skeleton--badge {
	width: 100px;
	height: 20px;
}

.txn-skeleton--amount {
	width: 110px;
	height: 24px;
}

.txn-skeleton--label {
	width: 64px;
	height: 12px;
}

.txn-skeleton--line {
	width: 100%;
	height: 14px;
}

.txn-skeleton--line-sm {
	width: 45%;
	height: 12px;
}

.txn-skeleton--input {
	width: 100%;
	max-width: 360px;
	height: 32px;
	border-radius: 4px;
}

.txn-skeleton--btn {
	width: 100%;
	height: 32px;
	border-radius: 4px;
}

.txn-skeleton--photo {
	width: 160px;
	height: 160px;
	border-radius: 10px;
}

.txn-skeleton--table-head {
	width: 100%;
	height: 32px;
}

.txn-skeleton--table-row {
	width: 100%;
	height: 22px;
}

@keyframes txn-skeleton-shimmer {
	0% {
		background-position: 200% 0;
	}

	100% {
		background-position: -200% 0;
	}
}
</style>
